import React, {
	useMemo,
	useCallback,
	useContext,
	useState,
	useEffect,
	useRef,
} from "react";
import _ from "lodash";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { Flex, Box } from "reflexbox";
import styled from "styled-components";
import Card from "../../UI/Card";
import Button from "../../UI/Button";
// import ContactForm from "./ContactForm";
import AddressForm from "./AddressForm";
import Field from "../../UI/forms/Field";
import { useQuery } from "../../Hooks";
import * as QMR from "../../../requests/quoteManagementRequests";
import * as CMR from "../../../requests/customerManagementRequests";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import { updateAddress } from "../../../requests/addressManagementRequests";
import { useTheme } from "styled-components";
import UserContext from "../../../context/user/UserContext";
import { useParams } from "react-router-dom";
import CreateShippingAddress from "./CreateShippingAddress";

const { getCustomers, updateCustomer } = CMR;
const { updateQuote } = QMR;

export const FieldWrapper = styled(Flex)`
	width: 100%;
	justify-content: flex-start;
	align-items: flex-start;

	form,
	div {
		width: 100%;
	}
`;

const validationSchema = yup.object().shape({
	customerId: yup.string().nullable(),
	shippingAddressId: yup.string().nullable(),
	sameAsBilling: yup.boolean().nullable(),
	contact: yup.object().shape({
		firstName: yup.string().nullable(),
		lastName: yup.string().nullable(),
		email: yup.string().email().nullable(),
		phone: yup.string().nullable(),
	}),
	customer: yup.object().shape({
		companyName: yup.string().nullable(),
	}),

	billing: yup.object().shape({
		Address: yup.string().nullable(),
		Address2: yup.string().nullable(),
		City: yup.string("Please enter a City").required("City is required."),
		County: yup.string().nullable(),
		State: yup
			.string("Please enter a Province/State")
			.required("Province/State is required."),
		Country: yup
			.string("Please enter a Country")
			.required("Country is required."),
		Zip: yup
			.string("Please enter a Postal/Zip Code")
			.required("Postal/Zip Code is required."),
	}),

	shipCode: yup.string().required(),
	shipping: yup.object().shape({
		Address: yup.string().nullable(),
		Address2: yup.string().nullable(),
		City: yup.string("Please enter a City").required("City is required."),
		County: yup.string().nullable(),
		State: yup
			.string("Please enter a Province/State")
			.required("Province/State is required."),
		Country: yup
			.string("Please enter a Country")
			.required("Country is required."),
		Zip: yup
			.string("Please enter a Postal/Zip Code")
			.required("Postal/Zip Code is required."),
	}),
});

const Customer = ({ isLoading, refetchQuote, refetchCustomer }) => {
	const { quoteId } = useParams();
	const { quote, setQuote } = useContext(ConfiguratorContext);
	const { activeUser } = useContext(UserContext);
	const { pid, groupIds } = activeUser || {};
	const [formToShow, setFormToShow] = useState("");
	const [editingId, setEditingId] = useState(null);

	const [shouldRefetch, setRefetch] = useState(null);
	const theme = useTheme();
	const throttle = useRef(null);

	useEffect(() => {
		switch (shouldRefetch) {
			case 0:
				// throttle.current = timeout;
				break;
			case 1:
				if (!throttle.current) {
					const timeout = setTimeout(() => {
						clearTimeout(throttle);
						setRefetch(2);
					}, 5000);
					throttle.current = timeout;
				}
				break;
			case 2:
				refetchQuote();
				clearTimeout(throttle.current);
				setRefetch(null);
				break;
			default:
				throttle.current = null;
				break;
		}
	}, [refetchQuote, shouldRefetch]);

	const { data, isLoading: customersLoading } = useQuery({
		request: getCustomers,
		skip: !pid,
		queryVariables: { pid, hasERP: true, groupIds, groupId: quote.groupId },
	});
	const { rows: customers = [] } = data || {};

	const customerOptions = useMemo(() => {
		return !customers
			? []
			: [
					...customers.map((row) => {
						const { billingAddress } = quote || {};
						const { customer } = row || {};
						const { id, erpId, companyName } = customer || {};

						const { Address, Address2, City, State, Zip } =
							billingAddress || {};

						let label = `${companyName || erpId || "—"}`;

						if (Address || Address2) {
							if (Address) label += `, ${Address}`;
							if (Address2) {
								label += ` ${Address2}`;
							}
							if (City) {
								label += `, ${City}`;
								if (State) {
									label += ` ${State}`;
									if (Zip) {
										label += `, ${Zip}`;
									}
								}
							}
						}

						return { label, value: id };
					}),
			  ];
	}, [customers, quote]);

	const onOptionClick = useCallback(
		async (id, resetForm) => {
			if (id === "") {
				await updateQuote({
					id: +quoteId || +quote?.id,
					input: { contactId: null },
				});
				refetchCustomer();
				setQuote({
					...quote,
					customer: null,
					billingAddress: null,
					shippingAddress: null,
				});
			} else {
				await updateQuote({
					id: +quoteId || +quote?.id,
					input: { contactId: id },
				});
			}
			setQuote(null);
			refetchQuote();
			resetForm();
		},
		[quote, quoteId, refetchCustomer, refetchQuote, setQuote]
	);

	const initialValues = useMemo(() => {
		const { firstName, lastName, email, phone, altPhone } =
			quote?.contact || {};
		const { companyName } = quote?.customer || {};
		const { shipCode } = quote || {};

		console.log({ quote });

		return {
			contact: {
				firstName: firstName || "",
				lastName: lastName || "",
				email: email || "",
				phone: phone || "",
				altPhone: altPhone || "",
			},
			customer: {
				companyName: companyName || "",
			},
			sameAsBilling: !!quote?.sameAsBilling ? true : false,
			shippingAddressId: quote?.shippingAddressId,
			customerId: quote?.contactId || "",
			billing: quote?.billingAddress,
			shipping: quote?.shippingAddress,
			shipCode: shipCode || "",
		};
	}, [quote]);

	const onSubmit = useCallback(
		async (formValue) => {
			const [key, value] = Object.entries(formValue)[0] || {};
			const { 0: recordType, 1: _recordKey } = key?.split(".");
			const recordKey = key?.split(".")?.length < 2 ? key : _recordKey;
			console.log({formValue});
			switch (recordType) {
				case "contact":
					try {
						setQuote({
							...quote,
							contact: { ...quote?.contact, [recordKey]: value },
						});
						await updateCustomer({
							id: quote?.contactId || quote?.customer?.id,
							input: { contact: { [recordKey]: value } },
						});
					} catch (error) {
						console.error(error);
					}
					break;
				case "billing":
					try {
						setQuote({
							...quote,
							billingAddress: {
								...quote?.billingAddress,
								[recordKey]: value,
							},
						});
						await updateCustomer({
							id: quote?.contactId || quote?.customer?.id,
							input: { billingAddress: { [recordKey]: value } },
						});
					} catch (error) {
						console.error(error);
					}
					break;
				case "shipping":
					try {
						setQuote({
							...quote,
							shippingAddress: {
								...quote?.shippingAddress,
								[recordKey]: value,
							},
						});
						if (quote?.shippingAddress?.id !== quote?.shippingAddressId) {
							await updateQuote({
								id: +quoteId,
								input: { shippingAddressId: quote?.shippingAddress?.id },
							});
						}
						await updateAddress({
							id: quote?.shippingAddress?.id,
							input: {
								[recordKey]: value,
							},
						});
					} catch (error) {
						console.error(error);
					}
					break;
				case "customer":
					try {
						setQuote({
							...quote,
							customer: {
								...quote?.customer,
								[recordKey]: value,
							},
						});
						await updateCustomer({
							id: quote?.contactId || quote?.customer?.id,
							input: { [recordKey]: value },
						});
					} catch (error) {
						console.error(error);
					}
					break;
				default:
					let val = value;
					setQuote({
						...quote,
						[key]: val,
					});
					if (`${key}` === "sameAsBilling") {
						if (value === "true") val = "0";
						if (value === true) val = "0";
						if (value === "True") val = "0";
						if (value === "false") val = "1";
						if (value === "False") val = "1";
						if (value === false) val = "1";

						if (val === "1") {
							setQuote({
								...quote,
								sameAsBilling: val,
								shippingAddress: quote?.billingAddress,
							});
						} else {
							setQuote({
								...quote,
								sameAsBilling: val,
								shippingAddress: quote?.shippingAddress,
							});
						}

						return await updateQuote({
							id: +quoteId,
							input: {
								[key]: val,
							},
						}); 
					}

					if (`${key}` === "customerId") {
						await updateQuote({
							id: +quoteId,
							input: {
								contactId: val,
							},
						});
					} else {
						await updateQuote({
							id: +quoteId,
							input: {
								[key]: val,
							},
						});
					}

					break;
			}
		},
		[quote, quoteId, setQuote]
	);

	const ref = useRef(null);
	const [customerTypedId, setCustomerTypedId] = useState("");

	const placeholder = useMemo(() => {
		if (!!customerTypedId) {
			const { 0: option } =
				_.filter(customerOptions, (op) =>
					op?.label?.startsWith(customerTypedId)
				) || {};

			return option?.label;
		}
		const op = quote?.contactId
			? _.find(customerOptions, ({ value }) => +value === +quote?.contactId)
			: _.find(customerOptions, ({ value }) => +value === +quote?.customer?.id);
		return op?.label || "Select a Customer";
	}, [customerOptions, customerTypedId, quote]);

	return (
		<>
			<Formik
				validationSchema={validationSchema}
				initialValues={initialValues}
				enableReinitialize={true}
				validateOnChange={false}
				validateOnBlur={false}
				validateOnMount={false}
				key={quote?.shippingAddressId}
			>
				{({ values, setFieldValue, resetForm }) => {
					return (
						<Box mt="2rem">
							{!!formToShow ? (
								<CreateShippingAddress
									{...{
										formToShow,
										setFormToShow,
										setEditingId,
										updateQuote,
										refetchQuote,
										refetchCustomer,
										setFieldValue
									}}
								/>
							) : (
								<>
									<Card>
										<Form style={{ width: "100%" }}>
											{!quote?.isPlane && (
												<Box textAlign={"right"}>
													<Button
														iconName="faPlus"
														color={theme.secondary}
														onClick={() => {
															setFormToShow("address");
														}}
													>
														{"Add Address"}
													</Button>
												</Box>
											)}
											<Box width={1} flex={1}>
												<Field
													ref={ref}
													typeableSelect
													onChange={(e) => {
														const label = e.target.value;
														const { value } =
															_.find(
																customerOptions,
																({ label: _label }) => _label === label
															) || {};
														setFieldValue("customerId", value);
														setCustomerTypedId(value);

														if (ref.current) clearTimeout(ref.current);

														ref.current = setTimeout(async () => {
															await onOptionClick(value, resetForm);
															setCustomerTypedId("");
														}, 500);
													}}
													options={customerOptions.map(({ label, value }) => (
														<option id={value} key={value} value={label}>
															{value}
														</option>
													))}
													name="customerId"
													labelText="Company"
													placeholder={placeholder}
													value={customerTypedId}
													isLoading={customersLoading}
													disabled={!!quote?.isPlane}
												/>
											</Box>

											{values.customerId && (
												<FieldWrapper
													width={1}
													flexDirection={["column", "column", "row"]}
												>
													<AddressForm
														disableForm={
															(isLoading ||
																quote?.contactId ||
																quote?.customer?.id) &&
															values["customerId"] !== ""
														}
														shippingAddresses={quote?.shippingAddresses}
														setRefetch={setRefetch}
														isLoading={isLoading}
														setQuote={setQuote}
														onSubmit={onSubmit}
														resetForm={resetForm}
														refetchQuote={refetchQuote}
														{...{
															formToShow,
															setFormToShow,
															setEditingId,
															editingId,
														}}
													/>
												</FieldWrapper>
											)}
										</Form>
									</Card>
								</>
							)}
						</Box>
					);
				}}
			</Formik>
		</>
	);
};

export default Customer;
